import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Check, CircleDashed, SendHorizonal, Waves } from "lucide-react";
import { api, dayjs, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal, SectionLabel } from "@/components/Reveal";

const CERT_THRESHOLD = ["janzu_surface", "janzu_immersion", "janzu_accompagnement"];

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [formations, setFormations] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/formations"),
      api.get("/enrollments/mine"),
      api.get("/posts"),
    ])
      .then(([f, e, p]) => {
        setFormations(f.data);
        setEnrollments(e.data);
        setPosts(p.data);
      })
      .catch(() => {});
  };

  useEffect(load, []);

  const completed = formations.filter((f) => user?.completed_modules?.includes(f.lookup_key));
  const hours = completed.reduce((a, f) => a + (f.hours || 0), 0);
  const certified = CERT_THRESHOLD.every((k) => user?.completed_modules?.includes(k));
  const upcoming = enrollments.filter((e) => e.session);
  const firstName = (user?.name || "").split(" ")[0];

  const toggle = async (lookupKey) => {
    try {
      const { data } = await api.post("/progression/toggle", { formation_slug: lookupKey });
      setUser(data);
      load();
    } catch (e) {
      console.error(formatApiError(e));
    }
  };

  const publish = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts", { content: draft.trim() });
      setPosts([data, ...posts]);
      setDraft("");
    } catch (e) {
      console.error(formatApiError(e));
    }
    setBusy(false);
  };

  const statCard = (v, l, tid) => (
    <div className="glass-card rounded-2xl p-6" data-testid={tid}>
      <p className="font-serif text-3xl md:text-4xl text-aqua">{v}</p>
      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-foam/50">{l}</p>
    </div>
  );

  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="dashboard-page">
      <div className="mx-auto max-w-7xl px-5 pt-14 md:px-10">
        <Reveal>
          <SectionLabel>Mon espace praticien·ne</SectionLabel>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl font-light text-foam" data-testid="dashboard-title">
            Bonjour, <em className="italic text-aqua">{firstName}</em>.
          </h1>
          <p className="mt-3 text-sm text-foam/55">
            Votre progression dans le cursus, vos sessions réservées et la communauté OJANZU.
          </p>
        </Reveal>

        {/* stats */}
        <div className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {statCard(`${hours} h`, "Heures validées", "stat-hours")}
          {statCard(`${completed.length} / ${formations.length || 4}`, "Modules complétés", "stat-modules")}
          {statCard(certified ? "Oui" : "En cours", certified ? "Certification Praticien OJANZU" : "Parcours praticien", "stat-cert")}
          {statCard(`${upcoming.length}`, "Sessions réservées", "stat-sessions")}
        </div>

        <div className="mt-14 grid gap-14 lg:grid-cols-12">
          {/* progression */}
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl text-foam">Progression du cursus</h2>
              <ul className="mt-7 space-y-3" data-testid="progression-list">
                {formations.map((f) => {
                  const done = user?.completed_modules?.includes(f.lookup_key);
                  const booked = enrollments.some((e) => e.title === f.title);
                  return (
                    <li
                      key={f.slug}
                      data-testid={`progression-item-${f.slug}`}
                      className={`flex flex-col gap-4 rounded-2xl border p-5 transition-all duration-300 md:flex-row md:items-center md:justify-between ${
                        done ? "border-aqua/40 bg-aqua/5" : "border-aqua/12 bg-deep/30"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span
                          className={`mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full ${
                            done ? "bg-aqua text-abyss" : "border border-aqua/30 text-aqua"
                          }`}
                        >
                          {done ? <Check size={17} /> : <CircleDashed size={17} />}
                        </span>
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-aqua/80">
                            Module {f.module_code} · {f.hours} h
                          </p>
                          <p className="mt-0.5 font-serif text-xl text-foam">{f.title}</p>
                          {done ? (
                            <p className="mt-1 text-xs text-aqua">Module validé</p>
                          ) : booked ? (
                            <p className="mt-1 text-xs text-foam/50">Session réservée — à valider après le stage</p>
                          ) : (
                            <p className="mt-1 text-xs text-foam/50">Pas encore réservé</p>
                          )}
                        </div>
                      </div>
                      {done ? null : booked ? (
                        <button
                          data-testid={`progression-toggle-${f.slug}`}
                          onClick={() => toggle(f.lookup_key)}
                          className="shrink-0 rounded-full border border-aqua/40 px-5 py-2.5 text-xs font-semibold text-aqua transition-all duration-300 hover:bg-aqua hover:text-abyss"
                        >
                          Marquer comme validée
                        </button>
                      ) : (
                        <Link
                          to={`/formations/${f.slug}`}
                          data-testid={`book-link-${f.slug}`}
                          className="shrink-0 rounded-full bg-aqua px-5 py-2.5 text-xs font-semibold text-abyss transition-all duration-300 hover:bg-foam"
                        >
                          Réserver
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <h2 className="mt-14 font-serif text-2xl text-foam">Mes sessions réservées</h2>
              {upcoming.length === 0 ? (
                <div className="glass-card mt-6 rounded-2xl p-8 text-center" data-testid="no-sessions">
                  <Waves size={22} className="mx-auto text-aqua/70" />
                  <p className="mt-3 text-sm text-foam/55">
                    Aucune session pour l'instant — choisissez un module pour entrer dans l'eau.
                  </p>
                  <Link
                    to="/formations"
                    className="mt-5 inline-block rounded-full bg-aqua px-6 py-3 text-xs font-semibold text-abyss"
                    data-testid="go-formations-link"
                  >
                    Voir les formations
                  </Link>
                </div>
              ) : (
                <ul className="mt-6 space-y-3" data-testid="sessions-list">
                  {upcoming.map((e) => (
                    <li key={e.id} className="glass-card flex flex-col gap-2 rounded-2xl p-5 md:flex-row md:items-center md:justify-between" data-testid="session-item">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-aqua/80">Module {e.module_code}</p>
                        <p className="mt-0.5 font-serif text-lg text-foam">{e.title}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foam/65">
                        <Calendar size={13} className="text-aqua" />
                        {e.session.lieu} · {e.session.periode}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          </div>

          {/* community */}
          <div className="lg:col-span-5">
            <Reveal delay={0.05}>
              <h2 className="font-serif text-2xl text-foam">La communauté</h2>
              <p className="mt-2 text-sm text-foam/55">
                Le fil des praticien·ne·s OJANZU — partages de séances, ressentis, questions.
              </p>
              <div className="glass-card mt-6 rounded-2xl p-5">
                <textarea
                  data-testid="feed-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  maxLength={600}
                  placeholder="Partagez votre expérience de l'eau…"
                  className="w-full resize-none rounded-xl border border-aqua/20 bg-abyss/70 px-4 py-3 text-sm text-foam placeholder:text-foam/35 outline-none transition-all duration-300 focus:border-aqua"
                />
                <div className="mt-3 flex justify-between items-center">
                  <span className="font-mono text-[10px] text-foam/35">{draft.length}/600</span>
                  <button
                    data-testid="feed-submit"
                    onClick={publish}
                    disabled={busy || !draft.trim()}
                    className="inline-flex items-center gap-2 rounded-full bg-aqua px-5 py-2.5 text-xs font-semibold text-abyss transition-all duration-300 hover:bg-foam disabled:opacity-40"
                  >
                    <SendHorizonal size={13} /> Publier
                  </button>
                </div>
              </div>

              <ul className="mt-6 space-y-4" data-testid="feed-list">
                {posts.map((p) => (
                  <li key={p.id} className="rounded-2xl border border-aqua/12 bg-deep/30 p-5" data-testid="post-item">
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-aqua/15 font-serif text-sm text-aqua">
                        {p.author_name?.[0]}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foam">{p.author_name}</p>
                        <p className="font-mono text-[10px] text-foam/40">
                          {dayjs(p.created_at).format("D MMMM YYYY · HH:mm")}
                        </p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foam/75">{p.content}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
